/**
 * Centralized Table Configuration
 * Single source of truth for table headers and column mappings
 * 
 * To change a column name:
 * 1. Update the header in the headers array
 * 2. Update the corresponding key in propertyMap
 * 3. The change will be reflected in both table display and CSV exports
 * 
 * Example: To change "Account ID" to "AWS Account ID" in RDS table:
 * - Change headers[1] from "Account ID" to "AWS Account ID"
 * - Change propertyMap key from "Account ID" to "AWS Account ID"
 */

export const TABLE_CONFIG = {
  rds: {
    headers: [
      "Organization",
      "Account ID",
      "Account Name",
      "AWS Region",
      "RDS Instance ID",
      "Engine",
      "Engine Version",
      "Backup Retention",
      "Backup Status"
    ],
    propertyMap: {
      "Organization": "CloudOrganizationUid",
      "Account ID": "AccountId",
      "Account Name": "AccountName",
      "AWS Region": "AWSRegion",
      "RDS Instance ID": "RDSInstanceId",
      "Engine": "RDSInstanceEngine",
      "Engine Version": "RDSInstanceEngineVersion",
      "Backup Retention": "BackupRetentionPeriod",
      "Backup Status": "BackupStatus"
    }
  },

  accounts: {
    headers: [
      "Organization Name",
      "Organizational Unit",
      "Account ID",
      "Account Name",
      "Account Status",
      "Joined Date",
      "Joined Method"
    ],
    propertyMap: {
      "Organization Name": "CloudOrganizationName",
      "Organizational Unit": "OrganizationalUnitName",
      "Account ID": "AccountId",
      "Account Name": "AccountName",
      "Account Status": "AccountStatus",
      "Joined Date": "JoinedDate",
      "Joined Method": "JoinedMethod"
    }
  },

  ec2: {
    headers: [
      "Organization",
      "Account ID",
      "Account Name",
      "Instance ID",
      "Instance Name",
      "Instance Type",
      "State",
      "Launch Time"
    ],
    propertyMap: {
      "Organization": "CloudOrganizationUid",
      "Account ID": "AccountId",
      "Account Name": "AccountName",
      "Instance ID": "EC2InstanceId",
      "Instance Name": "EC2InstanceName",
      "Instance Type": "EC2InstanceType",
      "State": "EC2State",
      "Launch Time": "EC2LaunchTime"
    }
  },

  efs: {
    headers: [
      "Organization Name",
      "Account ID",
      "Account Name",
      "AWS Region",
      "File System ID",
      "Creation Time",
      "Backup Policy Status",
      "Size (GB)"
    ],
    propertyMap: {
      "Organization Name": "CloudOrganizationUid",
      "Account ID": "AccountId",
      "Account Name": "AccountName",
      "AWS Region": "AWSRegion",
      "File System ID": "FileSystemId",
      "Creation Time": "CreationTime",
      "Backup Policy Status": "BackupPolicyStatus",
      "Size (GB)": "SizeInGb"
    }
  },

  s3: {
    headers: [
      "Organization",
      "Account ID",
      "Account Name",
      "Bucket Name",
      "Creation Date",
      "Block Public ACLs",
      "Ignore Public ACLs",
      "Block Public Policy",
      "Restrict Public Buckets"
    ],
    propertyMap: {
      "Organization": "CloudOrganizationUid",
      "Account ID": "AccountId",
      "Account Name": "AccountName",
      "Bucket Name": "BucketName",
      "Creation Date": "BucketCreationDate",
      "Block Public ACLs": "BlockPublicAcls",
      "Ignore Public ACLs": "IgnorePublicAcls",
      "Block Public Policy": "BlockPublicPolicy",
      "Restrict Public Buckets": "RestrictPublicBuckets"
    }
  },

  ad: {
    headers: [
      "Name",
      "User Name",
      "Status",
      "Email",
      "Description",
      "Created Date",
      "Modified Date",
      "Password Expires"
    ],
    propertyMap: {
      "Name": "ObjectName",
      "User Name": "SamAccountName",
      "Status": "Enabled",
      "Email": "EmailAddress",
      "Description": "Description",
      "Created Date": "CreatedDate",
      "Modified Date": "ModifiedDate",
      "Password Expires": "PasswordExpires"
    }
  },

  costs: {
    headers: [
      "Organization",
      "OPCO",
      "Account ID",
      "Account Name",
      "Product Name",
      "Unblended Cost",
    ],
    propertyMap: {
      "Organization": "CloudOrganizationUid",
      "OPCO":"OpcoName",
      "Account ID": "AccountId",
      "Account Name": "AccountName",
      "Product Name": "ProductName",
      "Unblended Cost": "UnblendedCost",
    }
  }
};

/**
 * Filter Options Configuration
 * Centralized configuration for all dropdown filters
 */

export const ORGANIZATION_OPTIONS = [
  { value: "", label: "All Organizations" },
  { value: "Liberty Latin America", label: "LLA - Liberty Latin America" },
  { value: "Liberty Costa Rica", label: "LCR - Liberty Costa Rica" }
];

export const EC2_STATE_OPTIONS = [
  { value: "", label: "All States" },
  { value: "running", label: "Running" },
  { value: "stopped", label: "Stopped" },
  { value: "pending", label: "Pending" },
  { value: "terminated", label: "Terminated" }
];

export const BOOLEAN_OPTIONS = [
  { value: "", label: "All Settings" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" }
];

export const IAM_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" }
];

export const BACKUP_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "ENABLED", label: "ENABLED" },
  { value: "DISABLED", label: "DISABLED" }
];

export const AD_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "true", label: "Enabled" },
  { value: "false", label: "Disabled" }
];



/**
 * Helper function to get table configuration
 * @param {string} tableType - The type of table (rds, accounts, ec2, etc.)
 * @returns {Object} Configuration object with headers and propertyMap
 */
export const getTableConfig = (tableType) => {
  const config = TABLE_CONFIG[tableType];
  if (!config) {
    throw new Error(`Table configuration not found for type: ${tableType}`);
  }
  return config;
};
